import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getRewardsMe, type EffectiveRewardRow } from "@/api/rewards";

/** "reading_comprehension" → "Reading comprehension". */
function humanizeType(t: string): string {
  const s = t.replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Collapse the per-(framework, type) rows to one representative rate per type
 *  (rounded average of the effective per-correct minutes across frameworks). */
function ratesByType(rows: EffectiveRewardRow[]): { type: string; minutes: number }[] {
  const acc = new Map<string, { sum: number; n: number }>();
  for (const r of rows) {
    const cur = acc.get(r.question_type) ?? { sum: 0, n: 0 };
    cur.sum += r.effective_minutes_per_correct;
    cur.n += 1;
    acc.set(r.question_type, cur);
  }
  return [...acc.entries()]
    .map(([type, { sum, n }]) => ({ type, minutes: Math.round(sum / n) }))
    .sort((a, b) => b.minutes - a.minutes);
}

/**
 * Slim, glanceable "what pays more" strip for the Train tab. Surfaces the
 * effort-aware earn rate from /rewards/me so the player can see why a reading
 * item is worth more unlock time than a flashcard. Non-critical: hides on error
 * or before the nightly job has populated any rows.
 */
export function RewardRateStrip() {
  const { t } = useTranslation();
  const me = useQuery({ queryKey: ["rewards-me"], queryFn: getRewardsMe });

  if (me.isError || !me.data) return null;
  const rates = ratesByType(me.data.per_type);
  if (rates.length === 0) return null;

  // Show the richest few plus the leanest one, so the spread (effort-awareness)
  // is visible without listing every type.
  const top = rates.slice(0, 5);
  const lean = rates[rates.length - 1];
  const shown =
    rates.length > 5 && !top.includes(lean) ? [...top, lean] : top;

  return (
    <div className="card mt-3 p-3">
      <div className="mb-2 text-xs font-semibold text-ink">{t("Earn rate by question type")}</div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {shown.map((r) => (
          <span
            key={r.type}
            className="shrink-0 rounded-full bg-brand-50 px-2.5 py-1 text-xs text-ink"
            title={t("{{type}}: ~{{count}} min per correct", { type: humanizeType(r.type), count: r.minutes })}
          >
            {humanizeType(r.type)}{" "}
            <span className="font-semibold text-brand-700">~{r.minutes}m</span>
          </span>
        ))}
      </div>
    </div>
  );
}
